import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { rqKeys } from '@/react-query-keys'
import { useAppStore } from '@/store'

/**
 * Хук для автоматической инвалидации и refetch всех запросов
 * при восстановлении сетевого соединения.
 *
 * Логика:
 * - Отслеживает события online/offline
 * - При переходе из offline в online:
 *   1. Делает refetch только активных И устаревших запросов
 *   2. Это обеспечивает получение свежих данных сразу после восстановления сети,
 *      но не дёргает то, что ещё не устарело
 */
export const useNetworkRefetch = () => {
  const queryClient = useQueryClient()
  const isAuthorized = useAppStore((s) => s.isAuthorized)
  const wasOffline = useRef(false)

  useEffect(() => {
    const handleOnline = async () => {
      // Если были офлайн и теперь онлайн — инвалидируем все и получаем свежие данные
      if (wasOffline.current) {
        console.info('[Network] Соединение восстановлено — обновляем данные')

        // Не рефетчим запросы в неавторизованном состоянии.
        // Иначе `client/visitor/info` может начать "дёргаться" из-за частых online/offline событий
        // (особенно на iOS), хотя пользователь ещё не вошёл.
        if (!isAuthorized) {
          wasOffline.current = false
          return
        }

        // Делаем refetch только активных и устаревших запросов (которые сейчас на экране)
        // Важно: НЕ делаем invalidateQueries() — иначе всё станет stale и stale:true перестанет иметь смысл
        // Certificates flow has dedicated offline queue sync in home.tsx.
        // Excluding certificate queries here avoids race: online refetch can momentarily
        // bring archived certificate back to active list before queue confirmation.
        await queryClient.refetchQueries({
          type: 'active',
          stale: true,
          predicate: (query) => {
            const firstKey = query.queryKey?.[0]
            return (
              firstKey !== rqKeys.certificatesList
              && firstKey !== rqKeys.certificatesRest
            )
          },
        })
      }

      wasOffline.current = false
    }

    const handleOffline = () => {
      console.info('[Network] Соединение потеряно — используем кэш')
      wasOffline.current = true
    }

    // Инициализируем состояние
    wasOffline.current = !navigator.onLine

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [ queryClient, isAuthorized ])
}
